import { motion } from "framer-motion";
import { CheckCircle, Warning, XCircle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatMoney, formatSignedMoney } from "./utils";
import type { DayCloseLabels } from "./types";

export interface VarianceSummaryBarProps {
  expectedMinor: number;
  countedMinor: number;
  varianceMinor: number;
  status: "ok" | "warning" | "blocked";
  labels: DayCloseLabels;
  locale: "tr" | "en";
  className?: string;
}

export function VarianceSummaryBar({
  expectedMinor, countedMinor, varianceMinor, status, labels, locale, className,
}: VarianceSummaryBarProps) {
  const Icon = status === "ok" ? CheckCircle : status === "warning" ? Warning : XCircle;
  const statusColor =
    status === "ok"
      ? "text-biz-success bg-biz-success-subtle border-biz-success-border"
      : status === "warning"
      ? "text-biz-warning bg-biz-warning-subtle border-biz-warning-border"
      : "text-biz-danger bg-biz-danger-subtle border-biz-danger-border";

  const dotColor =
    status === "ok" ? "bg-biz-success" : status === "warning" ? "bg-biz-warning" : "bg-biz-danger";

  return (
    <div
      className={cn(
        "sticky top-0 z-10 flex flex-wrap items-center gap-4 px-6 py-3 bg-biz-surface-elevated border-b border-biz-border biz-shadow-sm",
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <span className="flex items-center gap-2">
        <span className={cn("w-2 h-2 rounded-full", dotColor)} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wider text-biz-foreground-muted">
          {labels.summary.status}
        </span>
      </span>

      <Stat label={labels.summary.expected} value={formatMoney(expectedMinor, labels.currencyPrefix, locale)} muted />
      <span className="text-biz-foreground-subtle" aria-hidden>→</span>
      <Stat label={labels.summary.counted} value={formatMoney(countedMinor, labels.currencyPrefix, locale)} bold />

      <motion.div
        key={varianceMinor}
        initial={{ scale: 0.95, opacity: 0.6 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={cn(
          "ml-auto flex items-center gap-2 h-9 px-3 rounded-full border font-mono-num text-sm font-semibold tabular-nums",
          statusColor,
        )}
      >
        <Icon weight={status === "ok" ? "regular" : "fill"} className="w-4 h-4" />
        <span>
          {labels.summary.variance}:{" "}
          {formatSignedMoney(varianceMinor, labels.currencyPrefix, locale)}
        </span>
      </motion.div>
    </div>
  );
}

function Stat({ label, value, bold, muted }: { label: string; value: string; bold?: boolean; muted?: boolean }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted">{label}</span>
      <span
        className={cn(
          "font-mono-num tabular-nums text-base",
          bold && "font-bold text-biz-foreground",
          muted && "text-biz-foreground-muted",
        )}
      >
        {value}
      </span>
    </div>
  );
}
